// Direct Bluetooth thermal printing (ESC/POS) via Web Bluetooth API.
// Works on Android Chrome for BLE printers. Falls back to RawBT app scheme.

// Common GATT service UUIDs exposed by 58mm BLE thermal printers.
const PRINTER_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ffe5-0000-1000-8000-00805f9b34fb",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
];

let cachedDevice = null;
let cachedChar = null;

export function isWebBluetoothSupported() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

async function findWritableChar(server) {
  let services = [];
  try {
    services = await server.getPrimaryServices();
  } catch (e) {
    services = [];
  }
  for (const svc of services) {
    let chars = [];
    try {
      chars = await svc.getCharacteristics();
    } catch (e) {
      continue;
    }
    for (const c of chars) {
      if (c.properties.write || c.properties.writeWithoutResponse) return c;
    }
  }
  return null;
}

// Prompt the user to pick a printer (must be called from a user gesture).
export async function connectPrinter() {
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICES,
  });
  cachedDevice = device;
  device.addEventListener("gattserverdisconnected", () => {
    cachedChar = null;
  });
  const server = await device.gatt.connect();
  const ch = await findWritableChar(server);
  if (!ch) throw new Error("Karakteristik printer tidak ditemukan");
  cachedChar = ch;
  return device;
}

async function ensureChar() {
  if (cachedChar && cachedChar.service?.device?.gatt?.connected) return cachedChar;
  if (cachedDevice) {
    try {
      const server = await cachedDevice.gatt.connect();
      const ch = await findWritableChar(server);
      if (ch) {
        cachedChar = ch;
        return ch;
      }
    } catch (e) {
      // fall through to fresh pairing
    }
  }
  await connectPrinter();
  return cachedChar;
}

// Convert receipt text to ESC/POS byte stream (init + text + feed + cut).
function encodeReceipt(text) {
  const bytes = [0x1b, 0x40]; // ESC @ (initialize)
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    bytes.push(code > 0xff ? 0x3f : code); // '?' for non-latin chars
  }
  bytes.push(0x0a, 0x0a, 0x0a, 0x0a); // feed
  bytes.push(0x1d, 0x56, 0x42, 0x00); // GS V B 0 (partial cut, ignored if no cutter)
  return new Uint8Array(bytes);
}

async function writeChunks(ch, data) {
  const CHUNK = 180; // safe payload size for BLE MTU
  const useNoResponse = ch.properties.writeWithoutResponse;
  for (let i = 0; i < data.length; i += CHUNK) {
    const slice = data.slice(i, i + CHUNK);
    if (useNoResponse && ch.writeValueWithoutResponse) {
      await ch.writeValueWithoutResponse(slice);
    } else {
      await ch.writeValue(slice);
    }
    await new Promise((r) => setTimeout(r, 20));
  }
}

export async function printReceipt(text) {
  const ch = await ensureChar();
  await writeChunks(ch, encodeReceipt(text));
}

// Fallback: open RawBT app via URL scheme.
export function rawbtFallback(text) {
  const base64 = btoa(unescape(encodeURIComponent(text)));
  const link = document.createElement("a");
  link.href = `rawbt:base64,${base64}`;
  link.click();
}

// High-level: try Web Bluetooth, fall back to RawBT. Handles toasts.
export async function printReceiptSmart(text, toast) {
  if (isWebBluetoothSupported()) {
    try {
      toast?.info("Menghubungkan ke printer Bluetooth...");
      await printReceipt(text);
      toast?.success("Struk terkirim ke printer!");
      return;
    } catch (e) {
      if (e && e.name === "NotFoundError") {
        toast?.error("Tidak ada printer dipilih.");
        return;
      }
      toast?.error("Gagal via Bluetooth langsung, membuka RawBT...");
    }
  }
  rawbtFallback(text);
}
